import React, { ReactNode, useContext, useEffect, useRef, useState } from "react"
import { ChannelNameWithParams, Subscription } from "@rails/actioncable"
import { store } from "../../../app/state/store"
import LoadingSpinner from "../ui/LoadingSpinner"
import { CableContext } from "./CableContext"
import { useAppSelector } from "../../hooks"
import { mfaSmsRequesting } from "../../selectors/userSession"
import {
  registerCableSubscription,
  unregisterCableSubscription
} from "./cableMiddleware"

interface Props {
  channelName: string
  actionPrefix: string
  children?: ReactNode
  resourceId?: string
  awaitConnection?: boolean
  tenantId?: string
}

const CableConnection: React.FC<Props> = (props: Props): JSX.Element => {
  const { apiToken, consumer } = useContext(CableContext)
  const [isConnected, setIsConnected] = useState(false)
  const isConnectedRef = useRef(false)
  isConnectedRef.current = isConnected
  const requestingSms = useAppSelector(mfaSmsRequesting)

  useEffect(() => {
    if (!apiToken || apiToken.length === 0 || !consumer || requestingSms) {
      setIsConnected(false)
      return
    }

    const channelParams: ChannelNameWithParams = {
      channel: props.channelName
    }
    if (props.resourceId) channelParams.resource_id = props.resourceId
    if (props.tenantId) channelParams.tenant_id = props.tenantId

    const subId = `${props.channelName}-${props.resourceId || ""}-${props.tenantId || ""}-${Math.random().toString(36).substring(2, 9)}`

    const subscription: Subscription = consumer.subscriptions.create(
      channelParams,
      {
        connected() {
          setIsConnected(true)
        },
        disconnected() {
          console.error(`Disconnected ws connection: ${props.channelName}`)
          setIsConnected(false)
        },
        rejected() {
          console.error(`Rejected ws connection: ${props.channelName}`)
          setIsConnected(false)
        },
        received(data: unknown) {
          if (data && typeof data === "object" && "type" in data) {
            const action = data as { type: string; meta?: Record<string, unknown>; [key: string]: unknown }
            store.dispatch({
              ...action,
              meta: {
                ...(action.meta || {}),
                fromServer: true,
                __cablecar__: true,
                channel: props.channelName
              }
            })
          }
        }
      }
    )

    registerCableSubscription({
      id: subId,
      channelName: props.channelName,
      actionPrefix: props.actionPrefix,
      subscription,
      isConnected: () => isConnectedRef.current
    })

    return () => {
      // setTimeout to kill cable connection after final webRTC
      // events have been sent eg. to show user offline
      setTimeout(() => {
        unregisterCableSubscription(subId)
        subscription.unsubscribe()
      })
    }
  }, [consumer, apiToken, requestingSms, props.channelName, props.actionPrefix, props.resourceId, props.tenantId])

  if (props.awaitConnection && !isConnected && props.children) {
    return <LoadingSpinner />
  }
  if (!props.children) return null
  if (!apiToken || apiToken.length === 0 || !consumer) return null

  return (
    <div>
      {props.children}
    </div>
  )
}

export default CableConnection
